import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { getOrCreateChatKeyPair } from '@/services/chatCryptoKeys';
import { decryptChatPayload, deriveChatAesKey } from '@/services/chatMessageCrypto';
import {
  parseDecryptedChatPayload,
  type ParsedChatPayload,
} from '@/services/chatMessagePayload';
import { p2pThreadId, type P2PRawMessage } from '@/services/chatMessagesFirestore';

export function truncateNotificationPreview(text: string, maxLen = 120): string {
  const s = text.replace(/\s+/g, ' ').trim();
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
}

function summarizeAttachmentKind(kind: 'image' | 'video' | 'file', t: (k: string) => string): string {
  if (kind === 'image') return t('chats.replyPreviewPhoto');
  if (kind === 'video') return t('chats.replyPreviewVideo');
  return t('chats.replyPreviewFile');
}

export function notificationBodyFromParsed(parsed: ParsedChatPayload, t: (k: string) => string): string {
  if (parsed.kind === 'text') {
    return truncateNotificationPreview(parsed.text);
  }
  if (parsed.kind === 'text_media') {
    const caption = parsed.text.trim();
    if (caption.length > 0) return truncateNotificationPreview(caption);
    const k0 = parsed.attachments[0]?.kind;
    return k0 ? summarizeAttachmentKind(k0, t) : t('chats.notificationBody');
  }
  if (parsed.kind === 'media') {
    return summarizeAttachmentKind(parsed.attachment.kind, t);
  }
  return t('chats.notificationBody');
}

/** Открытый чат: ключ AES уже есть в хуке. */
export async function buildNotificationBodyFromRawWithKey(
  raw: P2PRawMessage | null | undefined,
  aesKey: CryptoKey | null,
  t: (k: string) => string
): Promise<string> {
  const fallback = t('chats.notificationBody');
  if (!raw) return fallback;
  if (raw.legacyText && !raw.cipherTextB64) {
    return truncateNotificationPreview(raw.legacyText);
  }
  if (!raw.cipherTextB64 || !raw.ivB64) return fallback;
  if (!aesKey) return fallback;
  try {
    const decrypted = await decryptChatPayload(aesKey, raw.cipherTextB64, raw.ivB64);
    const parsed = parseDecryptedChatPayload(decrypted);
    return notificationBodyFromParsed(parsed, t);
  } catch {
    return fallback;
  }
}

/** Список чатов: ключ деривируем по peerUid (контакт или inviteLookup). */
export async function buildNotificationBodyFromRawForInbox(params: {
  uid: string;
  myInviteToken: string;
  peerInviteToken: string;
  raw: P2PRawMessage;
  resolvePeerUid: () => Promise<string | null>;
  t: (k: string) => string;
}): Promise<string> {
  const fallback = params.t('chats.notificationBody');
  const { raw, t } = params;
  if (raw.legacyText && !raw.cipherTextB64) {
    return truncateNotificationPreview(raw.legacyText);
  }
  if (!raw.cipherTextB64 || !raw.ivB64) return fallback;
  const peerUid = await params.resolvePeerUid();
  if (!peerUid) return fallback;
  const threadId = p2pThreadId(params.myInviteToken, params.peerInviteToken);
  const keyRef = doc(db, 'userChatKeys', peerUid);
  const keySnap = await getDoc(keyRef);
  const jwk = keySnap.exists()
    ? (keySnap.data() as { chatPublicKeyJwk?: JsonWebKey }).chatPublicKeyJwk
    : undefined;
  if (!jwk || typeof jwk !== 'object') return fallback;
  try {
    const pair = await getOrCreateChatKeyPair(params.uid);
    const key = await deriveChatAesKey(pair.privateKey, jwk, threadId);
    const decrypted = await decryptChatPayload(key, raw.cipherTextB64, raw.ivB64);
    const parsed = parseDecryptedChatPayload(decrypted);
    return notificationBodyFromParsed(parsed, t);
  } catch {
    return fallback;
  }
}
